import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getModuleConfig, type ModuleConfigView } from '../../../api/Navigation/moduleConfig';

/**
 * Which product modules this company actually has.
 *
 * The same question `useFilteredNavigation` asks before drawing the sidebar, and deliberately the
 * same fail-open answer: a module the Operator has never configured counts as enabled, so only an
 * explicit `isEnabled: false` row hides anything and a company with no config behaves exactly as it
 * does today.
 *
 * Kept as its own small hook rather than a second `useFilteredNavigation()` call because the
 * dashboard needs the module set and nothing else — not the page config, the role filter, or the
 * SignalR subscription that hook also carries, all of which would be duplicated work here.
 */
export type UseEnabledModulesResult = {
  /** False until the company's config has been fetched (or the fetch has failed). */
  resolved: boolean;
  isModuleEnabled: (moduleId: string) => boolean;
};

export function useEnabledModules(): UseEnabledModulesResult {
  const isLoggedIn = useSelector(
    (state: { user?: { isLoggedIn?: boolean } }) => state.user?.isLoggedIn,
  );

  const [config, setConfig] = useState<ModuleConfigView[]>([]);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) {
      setConfig([]);
      setResolved(true);
      return;
    }

    let cancelled = false;
    // getModuleConfig already swallows its own errors and returns [], which is the fail-open case.
    getModuleConfig()
      .then((rows) => {
        if (!cancelled) setConfig(rows);
      })
      .finally(() => {
        if (!cancelled) setResolved(true);
      });

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  const isModuleEnabled = useCallback(
    (moduleId: string) => {
      const row = config.find((c) => c.moduleId === moduleId);
      return row ? row.isEnabled : true;
    },
    [config],
  );

  return { resolved, isModuleEnabled };
}

export default useEnabledModules;
